<template>
  <el-config-provider :locale="zhCn">
    <!-- 同步进度通知栏 -->
    <SyncProgressBar />
    <router-view
      v-if="isPublicPage"
      v-slot="{ Component }"
    >
      <transition
        name="fade-transform"
        mode="out-in"
      >
        <component :is="Component" />
      </transition>
    </router-view>

    <div
      v-else
      class="app-wrapper"
    >
      <!-- 侧边栏 -->
      <aside
        class="sidebar"
        :class="{ collapsed: isCollapsed }"
      >
        <div class="logo">
          <el-icon class="logo-icon">
            <WarningFilled />
          </el-icon>
          <span
            v-show="!isCollapsed"
            class="logo-text"
          >伟经电商监控系统</span>
        </div>
        <el-menu
          :default-active="activeMenu"
          :collapse="isCollapsed"
          :collapse-transition="false"
          router
          class="sidebar-menu"
        >
          <template
            v-for="item in userStore.menus"
            :key="item.id"
          >
            <el-sub-menu
              v-if="item.children && item.children.length > 0 && item.type !== 'F'"
              :index="item.path || `menu-${item.id}`"
            >
              <template #title>
                <el-icon v-if="item.icon">
                  <component :is="iconMap[item.icon] || 'Menu'" />
                </el-icon>
                <span>{{ item.name }}</span>
              </template>
              <el-menu-item
                v-for="child in visibleMenuItems(item.children)"
                :key="child.id"
                :index="child.path || `menu-${child.id}`"
              >
                <el-icon v-if="child.icon">
                  <component :is="iconMap[child.icon] || 'Menu'" />
                </el-icon>
                <template #title>
                  {{ child.name }}
                </template>
              </el-menu-item>
            </el-sub-menu>
            <el-menu-item
              v-else-if="item.type !== 'F'"
              :index="item.path || `menu-${item.id}`"
            >
              <el-icon v-if="item.icon">
                <component :is="iconMap[item.icon] || 'Menu'" />
              </el-icon>
              <template #title>
                {{ item.name }}
              </template>
            </el-menu-item>
          </template>
        </el-menu>
      </aside>

      <!-- 主内容区 -->
      <div
        class="main-container"
        :class="{ collapsed: isCollapsed }"
      >
        <!-- 顶部导航 -->
        <header class="navbar">
          <div class="navbar-left">
            <el-button
              link
              class="collapse-btn"
              @click="toggleSidebar"
            >
              <el-icon :size="20">
                <Fold v-if="!isCollapsed" />
                <Expand v-else />
              </el-icon>
            </el-button>
          </div>
          <div class="navbar-right">
            <el-tooltip
              content="全屏"
              placement="bottom"
            >
              <el-button
                link
                @click="toggleFullscreen"
              >
                <el-icon :size="18">
                  <FullScreen />
                </el-icon>
              </el-button>
            </el-tooltip>
            <el-dropdown trigger="click">
              <div class="user-info">
                <el-avatar
                  :size="32"
                  :icon="UserFilled"
                />
                <span class="username">{{ displayUsername }}</span>
                <el-icon><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item @click="router.push('/profile')">
                    <el-icon><User /></el-icon>
                    个人中心
                  </el-dropdown-item>
                  <el-dropdown-item @click="router.push('/profile')">
                    <el-icon><Setting /></el-icon>
                    系统设置
                  </el-dropdown-item>
                  <el-dropdown-item
                    divided
                    @click="handleLogout"
                  >
                    <el-icon><SwitchButton /></el-icon>
                    退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </header>

        <!-- 多页签 -->
        <TagsView />

        <!-- 内容区 -->
        <main class="app-main">
          <router-view v-slot="{ Component, route: viewRoute }">
            <transition
              name="fade-transform"
              mode="out-in"
            >
              <keep-alive :include="userStore.cachedViews">
                <component :is="Component" :key="viewRoute.path" />
              </keep-alive>
            </transition>
          </router-view>
        </main>
      </div>
    </div>
  </el-config-provider>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import {
  WarningFilled,
  Fold,
  Expand,
  FullScreen,
  UserFilled,
  ArrowDown,
} from '@element-plus/icons-vue'
import { getCurrentUser } from '@/api/auth'
import { useUserStore } from '@/store'
import { clearAuthStorage, hasValidStoredSession } from '@/utils/auth'
import SyncProgressBar from '@/components/SyncProgressBar.vue'
import TagsView from '@/components/TagsView.vue'
import type { MenuVO } from '@/types/menu'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const isCollapsed = ref(false)

const activeMenu = computed(() => route.path)
const isPublicPage = computed(() => !!route.meta.public)
const displayUsername = computed(() => userStore.username || '未登录')

const iconMap: Record<string, string> = {
  SettingsIcon: 'Setting',
  UserIcon: 'User',
  RoleIcon: 'Promotion',
  OrganizationIcon: 'OfficeBuilding',
  MenuIcon: 'Menu',
  DashboardIcon: 'Odometer',
  ShoppingCartIcon: 'Box',
  AlertIcon: 'Bell',
  TimelineIcon: 'DataAnalysis',
  RuleIcon: 'SetUp',
  ShieldIcon: 'FirstAidKit',
  GlobeIcon: 'Location',
  ShieldAlertIcon: 'Tickets',
  MonitorIcon: 'Monitor',
  LingxingIcon: 'Link',
  LingxingStoreIcon: 'Shop',
  LingxingOrderIcon: 'List',
  LingxingDataIcon: 'DataBoard',
  Connection: 'Connection',
  ShoppingCart: 'ShoppingCart',
  TrendCharts: 'TrendCharts',
  DataBoard: 'DataBoard',
}

function visibleMenuItems(children: MenuVO[]): MenuVO[] {
  return children.filter(child => child.type !== 'F' && child.visible === 1 && child.status === 1)
}

onMounted(async () => {
  if (!hasValidStoredSession() || userStore.userInfo) {
    return
  }

  try {
    const currentUser = await getCurrentUser()
    userStore.setUserInfo(currentUser)
  } catch {
    clearAuthStorage()
    if (route.path !== '/login') {
      router.push({
        path: '/login',
        query: { redirect: route.fullPath },
      })
    }
  }
})

// 切换侧边栏
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
}

// 全屏切换
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen()
  } else {
    document.exitFullscreen()
  }
}

// 退出登录
const handleLogout = () => {
  userStore.logout()
  ElMessage.success('退出登录成功')
  router.push('/login')
}
</script>

<style lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans SC', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-wrapper {
  display: flex;
  height: 100vh;
  width: 100%;

  .sidebar {
    width: 220px;
    height: 100%;
    background: linear-gradient(180deg, #0f1923 0%, #1a2332 100%);
    transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    flex-shrink: 0;
    position: relative;
    z-index: 100;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: 1px;
      background: linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
    }

    &.collapsed {
      width: 64px;

      .logo {
        .logo-text {
          opacity: 0;
          width: 0;
        }
      }
    }

    .logo {
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      background: rgba(0, 0, 0, 0.15);
      overflow: hidden;

      .logo-icon {
        font-size: 30px;
        color: #409EFF;
        margin-right: 10px;
        flex-shrink: 0;
        filter: drop-shadow(0 2px 4px rgba(64, 158, 255, 0.3));
      }

      .logo-text {
        font-size: 17px;
        font-weight: 700;
        color: #fff;
        white-space: nowrap;
        transition: all 0.3s ease;
        letter-spacing: 0.5px;
        opacity: 1;
      }
    }

    .sidebar-menu {
      border-right: none;
      height: calc(100% - 64px);
      overflow-y: auto;
      overflow-x: hidden;
      padding: 12px 8px;
      background-color: transparent;
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.08) transparent;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.08);
        border-radius: 4px;

        &:hover {
          background: rgba(255, 255, 255, 0.15);
        }
      }

      --el-menu-bg-color: transparent;
      --el-menu-hover-bg-color: rgba(255, 255, 255, 0.06);
      --el-menu-text-color: rgba(255, 255, 255, 0.6);
      --el-menu-active-color: #ffffff;

      .el-sub-menu__title {
        height: 46px;
        line-height: 46px;
        border-radius: 8px;
        margin-bottom: 2px;
        color: rgba(255, 255, 255, 0.6) !important;

        &:hover {
          background: rgba(255, 255, 255, 0.06) !important;
          color: #fff !important;
        }

        .el-icon {
          color: inherit !important;
        }
      }

      .el-menu-item {
        height: 44px;
        line-height: 44px;
        margin-bottom: 4px;
        border-radius: 8px;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        font-size: 14px;

        .el-icon {
          transition: transform 0.3s ease;
          font-size: 18px;
        }

        &:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.08) !important;

          .el-icon {
            transform: scale(1.15);
          }
        }

        &.is-active {
          background: linear-gradient(135deg, var(--el-color-primary) 0%, #5a8cff 100%) !important;
          box-shadow: 0 4px 14px rgba(64, 158, 255, 0.35);
          color: #ffffff;
          font-weight: 500;
          
          .el-icon {
            transform: scale(1.15);
            color: #ffffff !important;
          }
        }
      }
    }
  }

  .main-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .navbar {
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
      position: relative;
      z-index: 10;

      .navbar-left {
        display: flex;
        align-items: center;
        gap: 16px;

        .collapse-btn {
          font-size: 20px;
          color: #606266;
          transition: transform 0.3s ease;

          &:hover {
            color: var(--el-color-primary);
          }
        }
      }

      .navbar-right {
        display: flex;
        align-items: center;
        gap: 20px;

        .user-info {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 6px 12px;
          border-radius: 8px;
          transition: all 0.3s;

          &:hover {
            background: #f5f7fa;
          }

          .username {
            font-size: 14px;
            color: #606266;
            font-weight: 500;
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    .app-main {
      flex: 1;
      overflow: auto;
      background: #f0f2f5;
      padding: 0;
    }
  }
}

// Element Plus 菜单折叠样式覆盖
.el-menu--collapse {
  .el-sub-menu__title,
  .el-menu-item {
    justify-content: center;
    padding: 0 !important;

    .el-icon {
      margin: 0;
    }

    span {
      display: none;
    }
  }
}
</style>
